import { useHeader } from '@/contexts';
import {
  useBuscaApuracaoDetalhada,
  type BuscaApuracaoDetalhadaItem,
  type FiltrosApuracaoDetalhada,
} from '@/services/ApuracaoDetalhada';
import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react';

import {
  ApuracaoDetalhadaContext,
  type ApuracaoDetalhadaContextValue,
} from '@/contexts/ApuracaoDetalhadaContext';

const EMPTY_APURACAO_LISTA: BuscaApuracaoDetalhadaItem[] = [];

export function ApuracaoDetalhadaProvider({ children }: PropsWithChildren) {
  const { filtros } = useHeader();

  const [filtrosHeaderAplicados, setFiltrosHeaderAplicados] = useState<FiltrosApuracaoDetalhada>({
    empresaId: null,
    dataInicio: null,
    dataFim: null,
    periodoModo: null,
  });
  const [buscaAplicada, setBuscaAplicada] = useState(false);

  const aplicarTokenRef = useRef(0);
  const [aplicarToken, setAplicarToken] = useState(0);

  const selecaoMensalValida = useMemo(() => {
    return (
      !!filtros.empresaId &&
      filtros.periodoModo === 'mesano' &&
      !!filtros.dataInicio &&
      !!filtros.dataFim
    );
  }, [filtros.dataFim, filtros.dataInicio, filtros.empresaId, filtros.periodoModo]);

  const {
    data: apuracaoDetalhadaResponse,
    isLoading: isLoadingApuracaoDetalhada,
    isFetching: isFetchingApuracaoDetalhada,
    refetch: refetchApuracaoDetalhada,
  } = useBuscaApuracaoDetalhada(filtrosHeaderAplicados, { enabled: false });

  useEffect(() => {
    if (!buscaAplicada) return;
    if (aplicarToken === 0) return;
    if (aplicarTokenRef.current === aplicarToken) return;
    aplicarTokenRef.current = aplicarToken;
    refetchApuracaoDetalhada();
  }, [aplicarToken, buscaAplicada, refetchApuracaoDetalhada]);

  const handleBuscar = useCallback(() => {
    if (!selecaoMensalValida) {
      setBuscaAplicada(false);
      return;
    }

    setFiltrosHeaderAplicados({
      empresaId: filtros.empresaId,
      dataInicio: filtros.dataInicio,
      dataFim: filtros.dataFim,
      periodoModo: filtros.periodoModo,
    });
    setBuscaAplicada(true);
    setAplicarToken(prev => prev + 1);
  }, [
    filtros.dataFim,
    filtros.dataInicio,
    filtros.empresaId,
    filtros.periodoModo,
    selecaoMensalValida,
  ]);

  /*
    Decisão 11 da SPEC-017: depois de um "Buscar" no cabeçalho, as OUTRAS telas abrem já
    buscadas com o mesmo recorte, sem pedir de novo. `filtros.aplicado` é o que diz que
    houve aquele clique — e ele se desfaz quando a pessoa mexe no recorte, então isto não
    dispara enquanto ela escolhe.

    `buscaAplicada` na condição é o que impede o laço: `handleBuscar` o liga, e com ele
    ligado este efeito para de agir.
  */
  useEffect(() => {
    if (!filtros.aplicado) return;
    if (buscaAplicada) return;
    if (!selecaoMensalValida) return;
    handleBuscar();
  }, [filtros.aplicado, buscaAplicada, selecaoMensalValida, handleBuscar]);

  const handleLimparFiltrosGlobais = useCallback(() => {
    setBuscaAplicada(false);
    setFiltrosHeaderAplicados({
      empresaId: null,
      dataInicio: null,
      dataFim: null,
      periodoModo: null,
    });
    setAplicarToken(0);
    aplicarTokenRef.current = 0;
  }, []);

  const apuracaoLista = apuracaoDetalhadaResponse?.data?.lista ?? EMPTY_APURACAO_LISTA;
  const apuracaoTotais = apuracaoDetalhadaResponse?.data?.totais;
  const apuracaoSaidas = apuracaoDetalhadaResponse?.data?.saidas;

  // "Estorno de crédito" também contém "cr": o estorno é testado antes e não é crédito.
  const isEstorno = useCallback((tipo?: string) => {
    return (tipo || '').toLowerCase().includes('estorno');
  }, []);

  const isCredito = useCallback(
    (tipo?: string) => {
      return !isEstorno(tipo) && (tipo || '').toLowerCase().includes('cr');
    },
    [isEstorno]
  );

  const normalizeAliquota = useCallback((raw: number) => {
    if (!Number.isFinite(raw)) return 0;
    return raw > 0.02 && raw <= 1 ? raw / 100 : raw;
  }, []);

  const resumoMensal = useMemo(() => {
    const credito = { ibs: 0, cbs: 0 };
    const debito = { ibs: 0, cbs: 0 };
    const estorno = { ibs: 0, cbs: 0 };

    apuracaoLista.forEach(item => {
      const target = isEstorno(item.Tipo) ? estorno : isCredito(item.Tipo) ? credito : debito;
      target.ibs += Number(item.ValorIBS) || 0;
      target.cbs += Number(item.ValorCBS) || 0;
    });

    // O estorno reduz o crédito do período (SPEC-006, decisão 7).
    return {
      credito,
      debito,
      estorno,
      saldo: {
        ibs: Math.abs(debito.ibs - (credito.ibs - estorno.ibs)),
        cbs: Math.abs(debito.cbs - (credito.cbs - estorno.cbs)),
      },
    };
  }, [apuracaoLista, isCredito, isEstorno]);

  const isLoadingTabela = isLoadingApuracaoDetalhada || isFetchingApuracaoDetalhada;

  const value = useMemo(
    (): ApuracaoDetalhadaContextValue => ({
      selecaoMensalValida,
      buscaAplicada,
      filtrosAplicados: filtrosHeaderAplicados,
      handleBuscar,
      handleLimparFiltrosGlobais,
      apuracaoLista,
      apuracaoTotais,
      apuracaoSaidas,
      resumoMensal,
      isLoadingTabela,
      isCredito,
      isEstorno,
      normalizeAliquota,
    }),
    [
      apuracaoLista,
      apuracaoSaidas,
      apuracaoTotais,
      buscaAplicada,
      filtrosHeaderAplicados,
      handleBuscar,
      handleLimparFiltrosGlobais,
      isCredito,
      isEstorno,
      isLoadingTabela,
      normalizeAliquota,
      resumoMensal,
      selecaoMensalValida,
    ]
  );

  return (
    <ApuracaoDetalhadaContext.Provider value={value}>{children}</ApuracaoDetalhadaContext.Provider>
  );
}
